import { mergeStyleSets } from '@fluentui/react';
import { Alert, CloseIcon, Dialog, Flex, ProviderConsumer as FluentUIThemeConsumer, Loader, ThemePrepared } from '@fluentui/react-northstar';
import { themeNames } from '@fluentui/react-teams';
import { Action, Input, SubmitAction } from 'adaptivecards';
import _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import i18n from '../../../i18n/config';
import AppCSS from '../../App.module.css';
import { appState, AppStateValue, useSetState } from '../../AppState';
import { AdaptiveCardElement } from '../../templates/AdaptiveCardElement';
import { AddComment } from '../../templates/CustomComponents/AddComment';
import { Assignment } from '../../templates/CustomComponents/Assignment';
import { IDataSource } from '../../templates/CustomComponents/DataSource/DataSourceTable';
import { DueDate } from '../../templates/CustomComponents/Date/DueDate';
import { ResolutionDate } from '../../templates/CustomComponents/Date/ResolutionDate';
import { AffectedUsersPeoplePicker } from '../../templates/CustomComponents/PeoplePicker/AffectedUsersPeoplePicker';
import { CardPeoplePicker } from '../../templates/CustomComponents/PeoplePicker/CardPeoplePicker';
import { RequesterPeoplePicker } from '../../templates/CustomComponents/PeoplePicker/RequesterPeoplePicker';
import { CategoryPicker } from '../../templates/CustomComponents/Pickers/CategoryPicker';
import { GroupPicker } from '../../templates/CustomComponents/Pickers/GroupPicker';
import { PriorityPicker } from '../../templates/CustomComponents/Pickers/PriorityPicker';
import { StatusPicker } from '../../templates/CustomComponents/Pickers/StatusPicker';
import { TeamPicker } from '../../templates/CustomComponents/Pickers/TeamPicker';
import { TicketTypePicker } from '../../templates/CustomComponents/Pickers/TicketTypePicker';
import { RequestTitle } from '../../templates/CustomComponents/RequestTitle';
import { CollaboratorsTypeahead } from '../../templates/CustomComponents/Typeahead/CollaboratorsTypeahead';
import { TagsTypeahead } from '../../templates/CustomComponents/Typeahead/TagsTypeahead';
import { UploadFile, UploadFileProps } from '../../templates/CustomComponents/UploadFile';
import { Comment, Ticket } from '../interfaces/ticket.interface';
import { platformService } from '../services/platform.service';
import { uploadService } from '../services/upload.service';
import { mapPickerTreeNode, mapCardEnumProperties, formatDateInput, formatTimeInput, seedDesignerDefault, toNumberInputValue } from '../../templates/CustomComponents/Utils/CustomElementUtils';
import { CustomPropertyDataType_LABELS } from '../../custom-properties/utils/constants';
import { toastDefault } from '../utils/constants';
import { FileProps } from './EditTicketAttachments/EditTicketAttachments';
import { NewAttachmentFlowContent, startFileUploadSession } from './EditTicketAttachments/NewAttachmentFlow/NewAttachmentFlowContent';
import { TicketEntity } from './TicketForm';
import { CustomFormAttachmentsProps } from '../../templates/CustomForms/CustomForm.Interface';
interface TicketTemplateAnswerProps {
  globalTheme: ThemePrepared<any>;
  ticketId: number;
  parentData?: Ticket;
  isSavingState?: boolean;
  width?: number;
  isDisabled: boolean;
  isEndUserForm?: boolean;
  dispatchEvent?: any
}

const classNames = mergeStyleSets({
  customForm: {
    background: 'var(--mgt-theme-background) !important',
    '.ac-textBlock': {
      padding: '5px 0 !important',
      color: 'var(--mgt-theme-foreground) !important'
    },
    '.ac-richTextBlock > span': {
      color: 'var(--mgt-theme-foreground) !important'
    },
    '.ac-pushButton': {
      background: 'var(--mgt-theme-brand-background) !important'
    }
  },
  disabledRoot: {
    '.ac-pushButton': {
      pointerEvents: 'none !important',
      cursor: 'default !important'
    },
    '.ac-input': {
      pointerEvents: 'none !important',
      cursor: 'default !important'
    },
    '.ac-richTextBlock': {
      pointerEvents: 'none !important',
      cursor: 'default !important'
    }
  },
  disabledDefault: {
    '.ac-pushButton,.ac-input,.ac-input.ac-multichoiceInput.ac-choiceSetInput-compact': {
      background: 'rgb(240, 240, 240) !important',
    },
    '.tikit-typeahead': {
      '--selected-container-background-color': 'rgb(240, 240, 240) !important',
    },
    '.tikit-cardPicker': {
      '--input-background-color': 'rgb(240, 240, 240) !important',
    }
  },
  disabledDark: {
    '.ac-pushButton': {
      background: '#1f1f1f !important',
    },
    '.ac-input.ac-textInput, .ac-input.ac-multichoiceInput.ac-choiceSetInput-compact': {
      background: 'rgb(35, 35, 35) !important',
      color: 'rgb(198, 198, 198) !important'
    }
  }
});

export interface CustomFormUploadProps {
  setFilesToUpload?: any, setUploadFiles?: any, setOptionalMessage?: any, setIsPrivate?: any, currentFiles?: any, activeFormTemplateId?: number, setCustomFormAttachmentsProps?: any
}

export const TicketTemplateAnswer = (props: TicketTemplateAnswerProps) => {
  const { t } = useTranslation();
  const currentState = appState();
  const setState = useSetState();
  let { ticketId, parentData, isSavingState, width, isDisabled, isEndUserForm, dispatchEvent } = props;
  const api = new platformService();
  const uploadHelper = new uploadService();
  const [isLoading, setIsLoading] = useState(false);
  const defaultAlert = { message: '', success: true };
  const [alert, setAlert] = useState(defaultAlert);
  const [message, setMessage] = useState(t('ticket-details.edit-ticket-form.custom-form-answer.loading-custom-form-answer'));
  const [finalCardPayload, setFinalCardPayload] = useState({});
  const [updatedCardAnswerJson, setUpdatedCardAnswerJson] = useState({});
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState<FileProps[]>([]);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [optionalMessage, setOptionalMessage] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [ticketData, setTicketData] = useState<Ticket>({});
  const [submitData, setSubmitData] = useState({});

  let container = useRef<HTMLDivElement>(null);
  const actionSet = {
    type: 'ActionSet',
    actions: [
      {
        type: 'Action.Submit',
        title: t('ticket-details.edit-ticket-form.custom-form-answer.button.update-answer'),
        data: {
          commandId: 'CreateTicketFromTemplate'
        }
      }
    ]
  };

  const setPayloadData = (cardPayload, cardAnswerJson, currentFiles?: any) => {
    const extraProps: CustomFormUploadProps = {
      setFilesToUpload: setFilesToUpload,
      setUploadFiles: setUploadFiles,
      setOptionalMessage: setOptionalMessage,
      setIsPrivate: setIsPrivate,
      currentFiles: currentFiles
    }
    parseCard(cardPayload.body, cardAnswerJson, currentState, extraProps);
    if (!isDisabled && ticketId > 0)
      cardPayload.body.push(actionSet);

    setFinalCardPayload(cardPayload);
  }

  const loadData = async (data?: any) => {
    let ticket: Ticket = {};
    if (!data) {
      const result = await api.getTicket(props.ticketId);
      ticket = result.data;
    } else {
      ticket = data;
    }

    setTicketData(ticket);
    const cardPayload = JSON.parse(ticket.CardTemplateJson);
    const cardAnswer = ticket?.CardAnswerJson ?? "{}";
    const cardAnswerJson = JSON.parse((cardAnswer.length > 0 ? cardAnswer : "{}"));

    setPayloadData(cardPayload, cardAnswerJson);
    setUpdatedCardAnswerJson(cardAnswerJson);
    setState(s => ({...s, customTemplate: cardAnswerJson}))
    setTimeout(() => setIsLoading(false), !data ? 0 : 500);
  };

  useEffect(() => {
    if (isSavingState) {
      setIsLoading(true);
      setAlert(defaultAlert);
      loadData(parentData);
    }
  }, [parentData.CardTemplateJson, parentData.CardAnswerJson]);

  useEffect(() => {
    setIsLoading(true);
    setAlert(defaultAlert);
    loadData(parentData);
  }, []);

  useEffect(() => {
    if (!isLoading) {
      if (isDisabled) disableFields(true);
      else disableFields(false);
    }
  }, [isLoading, isSavingState]);

  const disableFields = (disabled: boolean) => {
    if (document.querySelector('.custom-form-answer')) {
      let parentDiv = document.querySelector('.custom-form-answer');
      const fieldSelectors = ['textarea', 'input', 'button', 'select'];
      fieldSelectors.forEach(selector => disableField(parentDiv, selector, disabled));
    }
  }

  const disableField = (parentDiv: Element, selector: string, disabled: boolean) => {
    if (parentDiv.querySelector(selector)) {
      let isTextarea = selector == "textarea";
      let disableType = isTextarea ? 'readonly' : 'disabled';
      let fields = parentDiv.querySelectorAll(selector);

      if (disabled) {
        fields.forEach(o => {
          o.setAttribute(disableType, 'true');
          if (isTextarea) {
            o.setAttribute('style', `${o.getAttribute("style")} pointer-events: revert !important`);
          }
        });
      }
      else fields.forEach(o => o.getAttribute(disableType) && o.removeAttribute(disableType));
    }
  }

  const disableUpdateButton = (disabled: boolean) => {
    let field = document.querySelector('.ac-pushButton');
    if (field) {
      if (disabled) field.setAttribute('disabled', 'true');
      else field.getAttribute('disabled') && field.removeAttribute('disabled');
    }
  }


  const onInputValueChanged = (input: Input) => {
    disableUpdateButton(false);
    let thisUpdatedCardAnswerJson = updatedCardAnswerJson;
    mapCardInputToAnswers(input, thisUpdatedCardAnswerJson);
    setUpdatedCardAnswerJson(thisUpdatedCardAnswerJson);

    //using dispatch properties in AddTicketDialog
    if (ticketId == 0)
      dispatchEvent({ action: "UpdateNewTicketCustomFormAnswer", property: "CardAnswerJson", data: JSON.stringify(thisUpdatedCardAnswerJson) });
  };

  const confirmUpdateAnswers = (submitData: any) => {
    const assignedValues = Object.assign(updatedCardAnswerJson, submitData);
    if (submitData[UploadFile.ElementId]?.length > 0 && filesToUpload.length > 0) {
      const isEndUserPage = false;
      const sendToChat = false;
      (async () => {
        try {
          setMessage(t('ticket-details.edit-ticket-form.custom-form-answer.saving-data-message'));
          setIsLoading(true);
          const filesToAttach = await startFileUploadSession(api, uploadHelper, filesToUpload, ticketId, false, "");
          let uploadedFiles = await api.attachTicketFiles(ticketId, filesToAttach, optionalMessage, isPrivate, isEndUserPage, sendToChat)

          let errorOcurred = false;
          setState((s) => ({
            ...s,
            uploadingState: {
              ...s.uploadingState,
              uploadedFiles: uploadedFiles.concat(s.uploadingState.uploadedFiles)
            }
          }));

          dispatchEvent({ type: "UpdateTicketPropertiesFromCustomForm", data: {} });
          toast.success(t('ticket-details.edit-ticket-form.custom-form-answer.success-response'), toastDefault);
        } catch (e) {
          toast.error(t('ticket-details.edit-ticket-form.custom-form-answer.update-error'), toastDefault);
        }

        loadData();
      })()
    }
    else
      (async () => {
        try {
          setMessage(t('ticket-details.edit-ticket-form.custom-form-answer.saving-data-message'));
          setIsLoading(true);
          const updatedData = { CardAnswerJson: JSON.stringify(assignedValues) };
          await api.updateCustomFormAnswers(props.ticketId, updatedData.CardAnswerJson);
          parentData = Object.assign(parentData, updatedData);
          let newData = {} as TicketEntity;
          mapCardAnswers(newData, JSON.stringify(finalCardPayload), updatedData.CardAnswerJson, currentState);

          //using dispatch properties in EditTicketForm
          dispatchEvent({ type: "UpdateTicketPropertiesFromCustomForm", data: newData });
          toast.success(t('ticket-details.edit-ticket-form.custom-form-answer.success-response'), toastDefault);
        } catch (e) {
          toast.error(t('ticket-details.edit-ticket-form.custom-form-answer.update-error'), toastDefault);
        }
        loadData();
      })();

    setOpenConfirmDialog(false);
  }

  const onExecuteAction = (action: Action) => {
    const submit = action as SubmitAction;
    const cardSubmitData = submit.data;
    formatInputRawData(finalCardPayload["body"], cardSubmitData);
    if (submit && cardSubmitData['commandId'] === 'CreateTicketFromTemplate') {
      delete cardSubmitData['commandId'];
      if (!JSON.stringify(cardSubmitData).includes("Ticket.")) {
        (async () => {
          try {
            const assignedValues = Object.assign(updatedCardAnswerJson, cardSubmitData);
            const updatedData = { CardAnswerJson: JSON.stringify(assignedValues) };
            setMessage(t('ticket-details.edit-ticket-form.custom-form-answer.saving-data-message'));
            setIsLoading(true);
            await api.updateTicket(props.ticketId, updatedData);
            parentData = Object.assign(parentData, updatedData);
            let newData = {} as TicketEntity;
            mapCardAnswers(newData, JSON.stringify(finalCardPayload), updatedData.CardAnswerJson, currentState);

            //using dispatch properties in EditTicketForm
            dispatchEvent({ type: "UpdateTicketPropertiesFromCustomForm", data: newData });
            toast.success(t('ticket-details.edit-ticket-form.custom-form-answer.success-response'), toastDefault);
          } catch (e) {
            toast.error(t('ticket-details.edit-ticket-form.custom-form-answer.update-error'), toastDefault);
          }
          loadData();
        })();
      }
      else {
        setSubmitData(cardSubmitData);
        setOpenConfirmDialog(true);
      }
    }
  };

  const onOpenUploadFromCardAction = () => {
    setOpenUploadDialog(!openUploadDialog);
  }

  const onCloseUploadFromCardAction = (currentFiles?: FileProps[], optionalMessage?: string, isPrivate?: boolean) => {
    setOpenUploadDialog(false);
    if (currentFiles?.length > 0) {
      const cardPayload = JSON.parse(ticketData?.CardTemplateJson);
      setPayloadData(cardPayload, updatedCardAnswerJson);

      setFilesToUpload(currentFiles);
      setOptionalMessage(optionalMessage);
      setIsPrivate(isPrivate);
    }
  }

  return (
    <FluentUIThemeConsumer render={globalTheme => {
      return (
        <>
          {isLoading && (
            <Flex className={`pt-5 mt-5`} styles={{ justifyContent: 'center' }}>
              <Loader label={message} />
            </Flex>
          )}
          {!isLoading && (
            <>
              <Flex column className={`mt-5 custom-form-answer`}>
                <AdaptiveCardElement
                  requestMappedText={" "}
                  keyString={"TemplateAnswer"}
                  jsonString={JSON.stringify(finalCardPayload)}
                  onExecuteAction={onExecuteAction}
                  onInputValueChanged={onInputValueChanged}
                  cardProps={{ styles: { height: 'fit-content', width: !width ? '40%' : `${width}%` } }}
                  containerClassName={`${classNames.customForm} 
                  ${!isDisabled && classNames.customForm}
                  ${isDisabled && classNames.disabledRoot}
                  ${isDisabled && globalTheme.siteVariables.theme === themeNames.Dark && classNames.disabledDark}
                  ${isDisabled && globalTheme.siteVariables.theme === themeNames.Default && classNames.disabledDefault}
                  `}
                  uploadFileProps={{ key: "TemplateAnswer", onOpenUploadFromCardAction: onOpenUploadFromCardAction, currentFiles: filesToUpload, message: optionalMessage, messageIsPrivate: isPrivate }}
                  isDisabled={isDisabled}
                  applyCustomVisibility={!isDisabled && (JSON.stringify(updatedCardAnswerJson)?.length ?? 0) > 0}
                  isEndUserPage={isEndUserForm}
                />
                <div>
                  {alert.message !== '' && alert.success && (<Alert content={alert.message} success dismissible />)}
                  {alert.message !== '' && !alert.success && (<Alert content={alert.message} danger dismissible />)}
                </div>
              </Flex>
              <Dialog
                className="w-full-important md:w-attachment max-h-screen md:max-h-template overflow-auto md:overflow-hidden"
                closeOnOutsideClick={false}
                open={openUploadDialog}
                content={{
                  content: <NewAttachmentFlowContent
                    globalTheme={globalTheme}
                    ticketId={ticketId}
                    filesToUpload={uploadFiles}
                    onInteraction={() => { }}
                    teamsConfig={currentState.teamsConfig}
                    isEndUserPage={isEndUserForm}
                    onClose={onCloseUploadFromCardAction}
                    uploadFromCustomFormProps={{ fromTemplate: true, attachToCustomFormFromWeb: true }}
                  />,
                  styles: { marginBottom: 0 },
                  className: `new-request-dialog md:overflow-auto`,
                  id: "new-cf-attachment",
                }}
                header={t('ticket-details.edit-ticket-form.custom-form-answer.addto-customform')}
                headerAction={{
                  icon: <CloseIcon />,
                  title: t("common.buttons.close"),
                  onClick: () => setOpenUploadDialog(false),
                }}
              />
              <Dialog
                className={`${AppCSS['confirmDialog']}`}
                cancelButton={t('knowledge.add-knowledge-modal.button.cancel')}
                confirmButton={t('common.buttons.confirm')}
                open={openConfirmDialog}
                onConfirm={() => {
                  if (submitData)
                    confirmUpdateAnswers(submitData)
                }}
                onCancel={() => {
                  setOpenConfirmDialog(false);
                }}
                header={t('ticket-details.edit-ticket-form.custom-form-answer.button.update-answer')}
                content={t('ticket-details.edit-ticket-form.custom-form-answer.update-answer-hint')}
                style={{ maxWidth: '500px' }}
              />
            </>
          )}
        </>
      );
    }}
    />
  );
};

export const updateRequestValue = (container: any[], data: any, currentState: AppStateValue, requestMappedText?: string, extraProps?: CustomFormUploadProps) => {
  container.forEach(item => {
    if ((item.type === 'Container' || item.type === 'Column') && item.items) {
      updateRequestValue(item.items, data, currentState, requestMappedText, extraProps);
    } else if (item.type === 'ColumnSet' && item.columns) {
      updateRequestValue(item.columns, data, currentState, requestMappedText, extraProps);
    } else if (item.id?.startsWith('Ticket.')) {
      if ([AddComment.ElementId,RequestTitle.ElementId].includes(item.id)) {
        if (data[item.id]?.includes("{{message}}") && item.contentField) {
          if (requestMappedText && requestMappedText.length > 0 && /\{{message\}}/ig.test(item.contentField)){
            let newValue = item.contentField.replace(/\{{message\}}/ig, requestMappedText.trim());
            if (AddComment.ElementId==item.id) {
              let commentValue = JSON.parse(item.value);
              commentValue.Body = newValue;
              item.value = JSON.stringify(commentValue);
            }
            else item.value = newValue;
          }
          else
            item.value = item.contentField.trim().length == 0 ? "" : item.contentField;
        } else
          item.value = data[item.id];

        data[item.id] = item.value;
      }
    }
  });
}

export const renderTimeInputInRequest = (timeString: string) => {
    const [hourString, minute] = timeString.split(":");
    const hour = +hourString % 24;
    return (hour % 12 || 12) + ":" + minute + (hour < 12 ? " AM" : " PM");
}

export const parseCard = (container: any[], data: any, currentState: AppStateValue, extraProps?: CustomFormUploadProps, dataSourceList?: any[]) => {
  container.forEach(item => {
    if ((item.type === 'Container' || item.type === 'Column') && item.items) {
      parseCard(item.items, data, currentState, extraProps, dataSourceList);
    } else if (item.type === 'ColumnSet' && item.columns) {
      parseCard(item.columns, data, currentState, extraProps, dataSourceList);
    } else if (item.type === 'Input.Toggle') {
      const defaultValue = item.isRequired ? '' : (item.value || item.valueOff || 'false');
      item.value = (data[item.id] || defaultValue);
    } else if (item.type === 'Input.Number') {
      const answer = toNumberInputValue(data[item.id]);
      item.value = answer !== '' ? answer : toNumberInputValue(item.value);
    } else if (item.type === 'Input.Time' && (data[item.id] || '').indexOf('T') > -1) {
      if ((data[item.id] || "").length > 0)
        item.value = formatTimeInput(data[item.id]);
      else if ((item?.value ?? "").length > 0)
        item.value = formatTimeInput(item.value);
      else
        item.value = "";
    } else if (item.type === 'Input.Date' && (data[item.id] || '').indexOf('T') > -1) {
      if ((data[item.id] || "").length > 0)
        item.value = formatDateInput(data[item.id]);
      else if ((item?.value ?? "").length > 0)
        item.value = formatDateInput(item.value);
      else
        item.value = "";
    } else if (item.type.startsWith('Input.')) {
      if (data.hasOwnProperty(item.id)) {
        item.value = data[item.id]
      } else {
        item.value = item.value || '';
      }
    } else if (item.type == CardPeoplePicker.JsonTypeName) {
      item.value = data[item.id] ?? item.value ?? '';
    } else if (item.id?.startsWith('Ticket.')) {
      if (item.id !== UploadFile.ElementId)
        seedDesignerDefault(item, data);
      switch (item.id) {
        case RequestTitle.ElementId:
          if (!data[item.id] && item.contentField)
            item.value = item.contentField.trim().length == 0 ? "" : item.contentField;
          else
            item.value = data[item.id];
          break;
        case AddComment.ElementId:
          if (!data[AddComment.ElementId] && !data[AddComment.ElementId + ".Body"] && item.contentField){
            const body = item.contentField.trim().length == 0 ? "" : item.contentField;
            const comment = { Body: body, IsPublic: true } as Comment;
            item.value = JSON.stringify(comment);
          } else if (data[AddComment.ElementId]) {
            try {
              item.value = data[AddComment.ElementId];
            } catch (e) {
              //ignore
              console.error(e);
            }
          } else if (data[AddComment.ElementId + ".Body"]) {
            const body = data[AddComment.ElementId + ".Body"];
            const isPrivate = data[AddComment.ElementId + ".Private"];
            const comment = { Body: body, IsPublic: isPrivate == "False" } as Comment;
            item.value = JSON.stringify(comment);
          }
          break;
        case UploadFile.ElementId:
          if (data[item.id]) {

            const dataItem = data[item.id];
            let uploadfileprops: UploadFileProps = (typeof (dataItem) == "string") ? JSON.parse(dataItem) : dataItem;

            const fullDetailAttachments = currentState?.uploadingState?.uploadedFiles ?? [];
            if (fullDetailAttachments.length > 0) {
              uploadfileprops.Files.forEach(f => {
                const detailedFile = fullDetailAttachments.find(a => a.Id == f.Id);
                if ((detailedFile?.Id ?? 0) > 0)
                  f.DownladUrl = detailedFile.DownladUrl;
              });
            }


            item.value = (typeof (dataItem) == "object") ? JSON.stringify(data[item.id]) : dataItem;

            if(extraProps?.activeFormTemplateId){
              const newFormAttachmentProps: CustomFormAttachmentsProps = {TemplateFormId: extraProps?.activeFormTemplateId, Files: uploadfileprops.Files, Message: uploadfileprops.Comment?.Body, IsPrivate: !(uploadfileprops.Comment?.IsPublic ?? false)};
              extraProps?.setCustomFormAttachmentsProps?.(newFormAttachmentProps);
            }
          }
          break;
        case StatusPicker.ElementId:
        case TicketTypePicker.ElementId:
        case CategoryPicker.ElementId:
        case PriorityPicker.ElementId:
          try {
            const keyValue = {};
            keyValue[StatusPicker.ElementId] = _.cloneDeep(currentState?.ticketStatus ?? []);
            keyValue[TicketTypePicker.ElementId] = _.cloneDeep(currentState?.ticketTypes ?? []);
            keyValue[CategoryPicker.ElementId] = _.cloneDeep(currentState?.categories ?? []);
            keyValue[PriorityPicker.ElementId] = _.cloneDeep(currentState?.priority ?? []);

            const dataSourceId = parseInt(item.dataSource);
            if (dataSourceId && dataSourceId > 0 && !item.choices) {
              const dataSource: IDataSource | null =  dataSourceList?.find(d => d.Id === dataSourceId) ?? null;
              
              if(dataSource != null){
                dataSource.SelectedValues = dataSource.SelectedItems.split(",") || [];
                let choicesSource = keyValue[item.id]?.filter(s => dataSource?.SelectedValues.includes(s.Id.toString())) ?? [];
                choicesSource = mapPickerTreeNode(choicesSource, keyValue[item.id]);
                if ((choicesSource?.length ?? 0) > 0)
                  item.choices = mapCardEnumProperties(choicesSource);
              }
            }

            const defaultChoice = keyValue[item.id].find(x => x["IsDefault"]);
            if (defaultChoice?.IsDefault && !data[item.id] && !item.requireField)
              data[item.id] = `${defaultChoice["Id"]}`;
          } catch (err) {
            console.error(err);
          }
          item.value = data[item.id];
          break;
        case TagsTypeahead.ElementId:
          try {
            const dataSourceId = parseInt(item.dataSource);
            if (dataSourceId && dataSourceId > 0 && !item.choices) {
              let tagsList = currentState?.tags ?? [];
              const dataSource: IDataSource = dataSourceList?.find(d => d.Id == dataSourceId);
              dataSource.SelectedValues = dataSource.SelectedItems.split(",") || [];
              let choicesSource = tagsList?.filter(s => dataSource.SelectedValues.includes(s.Id.toString())) ?? [];
              if ((choicesSource?.length ?? 0) > 0) {
                tagsList = mapCardEnumProperties(choicesSource, "Id", "Name");
                TagsTypeahead.pickerChoices = tagsList;
                item.choices = tagsList;
              }
            }
          } catch {
          }
          item.value = data[item.id];
          break;
        case TeamPicker.ElementId:
        case GroupPicker.ElementId:
          try {
            const dataSourceId = parseInt(item.dataSource);
            const dataList = (item.id == TeamPicker.ElementId) ? currentState?.teams.filter(t => (t["TeamsAadObjectId"]?.length ?? 0) > 0) : currentState?.supportgroups;
            let list: any[] = dataList ?? [];
            if (dataSourceId && dataSourceId > 0 && !item.choices) {
              const dataSource: IDataSource = dataSourceList?.find(d => d.Id == dataSourceId);
              dataSource.SelectedValues = dataSource.SelectedItems.split(",") || [];
              let choicesSource = list?.filter(s => dataSource.SelectedValues.includes(s.Id.toString())) ?? [];
              if ((choicesSource?.length ?? 0) > 0)
                item.choices = mapCardEnumProperties(choicesSource, "Id", (item.id == TeamPicker.ElementId) ? "FriendlyName" : "Name");
            }

            if (item.id == TeamPicker.ElementId) {
              const defaultChoice = list.find(x => x["IsDefault"]);
              if (defaultChoice?.IsDefault && !data[item.id] && !item.requireField)
                data[item.id] = `${defaultChoice["Id"]}`;
            }
          } catch (err) {
            console.error(err);
          }
          item.value = data[item.id];
          break;
        default:
          item.value = data[item.id];
          break;
      }
    } else if (item.isCustomProperty && item.dataType) {
      switch(CustomPropertyDataType_LABELS[item.dataType]){
        case CustomPropertyDataType_LABELS.INT:
          const answer = toNumberInputValue(data[item.id]);
          item.value = answer !== '' ? answer : toNumberInputValue(item.value);
          break;
        case CustomPropertyDataType_LABELS.LIST:
          seedDesignerDefault(item, data);
          try 
          {
            const propertyData = currentState?.customProperties.find(p => p.PropertyId == item.id);
            const customPropsDataList: any[] = _.cloneDeep(propertyData?.CustomPropertyListItems)
            const dataSourceId = parseInt(item.dataSource);
            if (dataSourceId && dataSourceId > 0 && !item.choices) {
              const dataSource: IDataSource = dataSourceList?.find(d => d.Id == dataSourceId);
              dataSource.SelectedValues = dataSource.SelectedItems.split(",") || [];
              let choicesSource = customPropsDataList?.filter(s => dataSource.SelectedValues.includes(s.Id.toString())) ?? [];
              choicesSource = mapPickerTreeNode(choicesSource, customPropsDataList);
              if ((choicesSource?.length ?? 0) > 0)
                item.choices = mapCardEnumProperties(choicesSource);
            }

            const defaultChoice = customPropsDataList?.find(x => x["IsDefault"]);
            if (defaultChoice?.IsDefault && !data[item.id] && !item.requireField)
              data[item.id] = `${defaultChoice["Id"]}`;
          } catch (err) {
            console.error(err);
          }
          item.value = data[item.id];
          break;
        default:
          item.value = data[item.id] || item.value || '';
          break;
      }
    }

    if (item.value == `$\{${item.id}\}`) item.value = "";

    if (item.id)
      data[item.id] = item.value || "";
  });
};

export const resetCard = (container: any[]) => {
  container.forEach(item => {
    if (item.type === 'Container' || item.type === 'Column') {
      resetCard(item.items);
    } else if (item.type === 'ColumnSet') {
      resetCard(item.columns);
    } else {
      item.value = "";
    }
  });
};

const mapCardAnswersStringValue = (cardTemplateJson: string, key: string, givenValue: string, currentState: AppStateValue) => {
  let value;
  const cardTemplateJsonValue = JSON.parse(cardTemplateJson); 
  let element;
  if(cardTemplateJsonValue?.body){
      const elements: any[] = cardTemplateJsonValue.body;
      element = elements.find(e => e["id"] == key);
  }
  switch(key){
    case RequestTitle.ElementId:
    case UploadFile.ElementId:
      value = "";
      break;
    case StatusPicker.ElementId:
      value = currentState?.ticketStatus?.find(t => t.Id.toString() == givenValue)?.Value ?? "";
      break;
    case TicketTypePicker.ElementId:
      value = currentState?.ticketTypes?.find(t => t.Id.toString() == givenValue)?.Value ?? "";
      break;
    case CategoryPicker.ElementId:
      value = currentState?.categories?.find(t => t.Id.toString() == givenValue)?.Value ?? "";
      break;
    case PriorityPicker.ElementId:
      value = currentState?.priority?.find(t => t.Id.toString() == givenValue)?.Value ?? "";
      break;
    case TagsTypeahead.ElementId:
      const tagIds = givenValue.split(",");
      const tags = currentState?.tags?.filter(t => tagIds.includes(t.Id.toString())) ?? [];
      value = tags?.map(t => t.Name)?.join(", ") ?? "";
      break;
    case CollaboratorsTypeahead.ElementId:
      const vals = givenValue.split(",");
      const users = currentState?.usersInRole?.filter(t => vals.includes(t.Id.toString())) ?? [];
      value = users?.map(t => t.FullName)?.join(", ") ?? "";
      break;
    case RequesterPeoplePicker.ElementId:
    case AffectedUsersPeoplePicker.ElementId:
      const aUsers = JSON.parse(givenValue);
      const nameUsers = aUsers?.map(a => a["FullName"]) ?? [];
      value = nameUsers?.join(", ") ?? "";
      break;
    case TeamPicker.ElementId:
      value = currentState?.teams?.find(t => t.Id.toString() == givenValue)?.FriendlyName ?? "";
      break;
    case GroupPicker.ElementId:
      value = currentState?.supportgroups?.find(t => t.Id.toString() == givenValue)?.Name ?? "";
      break;
    case Assignment.ElementId:
      value = currentState?.usersInRole?.find(t => t.Id.toString() == givenValue)?.FullName ?? "";
      break;
    case AddComment.ElementId:
        const comment: Comment = JSON.parse(givenValue);
        value = comment?.Body ?? "";
        break;
    case DueDate.ElementId:
    case ResolutionDate.ElementId:
        if((givenValue ?? "").length > 0)
          value = DateTime.fromISO(givenValue).setLocale(i18n.language).toFormat('EEE, MMM d, yyyy')
        break;
    default:
        if(element && (givenValue ?? "").length > 0) {
          if(element.type === 'Input.Time' || (element.isCustomProperty && CustomPropertyDataType_LABELS[element.dataType] == CustomPropertyDataType_LABELS.TIME))
            value = renderTimeInputInRequest(givenValue);
          else if(element.type === "Input.Date" || element.isCustomProperty && CustomPropertyDataType_LABELS[element.dataType] == CustomPropertyDataType_LABELS.DATE)
            value = DateTime.fromISO(givenValue).setLocale(i18n.language).toFormat('EEE, MMM d, yyyy')
          else if(element.isCustomProperty && CustomPropertyDataType_LABELS[element.dataType] == CustomPropertyDataType_LABELS.LIST){
            const cp = currentState?.customProperties?.find(p => p.PropertyId == key);
            if(cp?.CustomPropertyListItems){
              const givenValues = givenValue?.toString().split(",") ?? [];
              const cpItemValues = cp.CustomPropertyListItems?.filter(c => givenValues.includes(c.Id.toString()))?.map(p => p.Value) ?? [];
              value = cpItemValues?.join(", ") ?? "";
            }
          }else
            value = givenValue;
        }
        else
          value = givenValue;
        break;
  }

  return value;
}

const mapRequestFieldValueToCardAnwer = (value: string, cardTemplateJson: string, cardAnswerJson: string, currentState: AppStateValue) => {
  const cardAnswerJsonValue = JSON.parse(cardAnswerJson);
  const paramsPattern = /\{{([^}]+)\}}/g;
  let updateValue = value;
  const matches = value.matchAll(paramsPattern);
  for (const match of matches) {
    const replaceText = match[0];
    let replaceValue = match[1] ? cardAnswerJsonValue[match[1]] : "";
    replaceValue = replaceValue ? mapCardAnswersStringValue(cardTemplateJson, match[1], replaceValue, currentState) : "";
    updateValue = updateValue.replace(replaceText, replaceValue);
  }
  return updateValue;
}

export const mapCardInputToAnswers = (input: Input, cardAnswer: any) => {
  let inputId = input.id;
  if (input.id)
    cardAnswer[input.id] = input.value;
  else {
    const parent = input.parent;
    if (parent?.id) {
      cardAnswer[parent.id] = parent["value"];
      inputId = parent.id;
    }
  }
  return inputId;
}

export const getISODateOnlyPartString = (selectedDate: Date) => [selectedDate.getFullYear(),(selectedDate.getMonth()+1).toString().padStart(2, '0'), selectedDate.getDate().toString().padStart(2, '0')].join("-");

export const getCardDateInputISOFormatted = (dateString: string) => {
  //save card answer for dates in this format "yyyy-MM-dd'T'HH:mm:ss.fffzzz"; This includes user timezone.
  return DateTime.fromSQL(dateString).set({ hour: 0, minute: 0, second: 0 }).toISO();
}

export const getCardTimeInputISOFormatted = (timeString: any) => {
  //save card answer for dates in this format "yyyy-MM-dd'T'HH:mm:ssZ"; This is in UTC.
  const timeSplit = timeString.split(":");
  const todaysDate = new Date();
  const newDate = new Date(todaysDate.getFullYear(), todaysDate.getMonth(), todaysDate.getDate(), timeSplit[0], timeSplit[1], 0);
  return newDate.toISOString();  
}

export const formatInputRawData = (container: any[], data: any) => {
  container.forEach(item => {
    if ((item.type === 'Container' || item.type === 'Column') && item.items)  {
      formatInputRawData(item.items, data);
    } else if (item.type === 'ColumnSet' && item.columns) {
      formatInputRawData(item.columns, data);
    } else if ((data[item.id] || '').length > 0 &&
        (item.type === 'Input.Time' || (item.isCustomProperty && CustomPropertyDataType_LABELS[item.dataType] == CustomPropertyDataType_LABELS.TIME))) {
      const newValue = data[item.id];
      if(!isDateISOFormat(newValue)){
        data[item.id] = getCardTimeInputISOFormatted(newValue);
        data["UnformattedTime_" + item.id] = newValue;
      }
    } else if ((data[item.id] ?? '').length > 0 &&
        (item.type === 'Input.Date' || item.id === DueDate.ElementId || item.id === ResolutionDate.ElementId ||
        (item.isCustomProperty && CustomPropertyDataType_LABELS[item.dataType] == CustomPropertyDataType_LABELS.DATE))) {
      const newValue = data[item.id];
      const ACDesignerDatePattern = /^\d{4}\-(0[1-9]|1[012])\-(0[1-9]|[12][0-9]|3[01])$/;
      if(ACDesignerDatePattern.test(newValue))
        data[item.id] = getCardDateInputISOFormatted(newValue);
    }
  });
}

function isDateISOFormat(dateString) {
  const date = new Date(dateString);
  const isValidUTC = !isNaN(date.getTime()) && dateString.endsWith("Z");
  return isValidUTC;
}

export const mapCardAnswers = (ticketData: any, cardTemplateJson: string, cardAnswerJson: string, currentState: AppStateValue, addDialogData?: any) => {
  const cardAnswerJsonValue = JSON.parse(cardAnswerJson);
  const cardTemplateJsonValue = JSON.parse(cardTemplateJson);
  const customProperties = currentState?.customProperties ?? [];
  Object.keys(cardAnswerJsonValue).forEach(function (key) {
    const value = cardAnswerJsonValue[key];
    switch (key) {
      case RequestTitle.ElementId:
        ticketData.Title = mapRequestFieldValueToCardAnwer(value, cardTemplateJson, cardAnswerJson, currentState);
        break;
      case RequesterPeoplePicker.ElementId:
        try {
          ticketData.RequesterPicker = JSON.parse(value)[0];
        } catch (e) {
        }
        break;
      case AffectedUsersPeoplePicker.ElementId:
        try {
          ticketData.AffectedUsersPicker = JSON.parse(value) ?? [];
        } catch (e) {
        }
        break;
      case StatusPicker.ElementId:
        ticketData.StatusId = parseInt(value);
        break;
      case TicketTypePicker.ElementId:
        ticketData.TicketTypeId = parseInt(value);
        break;
      case CategoryPicker.ElementId:
        ticketData.CategoryId = parseInt(value);
        break;
      case PriorityPicker.ElementId:
        ticketData.PriorityId = parseInt(value);
        break;
      case TagsTypeahead.ElementId:
        const tagIds = value.split(",");
        ticketData.TagsPicker = tagIds.map(x => { return { "Id": parseInt(x) } });
        break;
      case CollaboratorsTypeahead.ElementId:
        const collabIds = value.split(",");
        ticketData.CollaboratorsPicker = collabIds.map(x => { return { "Id": parseInt(x) } });
        break;
      case TeamPicker.ElementId:
        const teamId = parseInt(value);
        if (teamId)
          ticketData.TeamId = teamId;
        const team = currentState?.teams.find(x => x.Id == teamId);
        if (team) {
          const group = currentState?.supportgroups.find(x => x.TeamsAadObjectId == team.TeamsAadObjectId);
          ticketData.SupportGroupId = group?.Id;
        }
        break;
      case GroupPicker.ElementId:
        const groupId = parseInt(value);
        ticketData.SupportGroupId = groupId
        const sg = currentState?.supportgroups.find(x => x.Id == groupId);
        if (sg) {
          const tm = currentState?.teams.find(x => x.TeamsAadObjectId == sg.TeamsAadObjectId);
          ticketData.TeamId = tm?.Id;

          const currentAssigneeId = addDialogData?.Entity?.AssigneeId ?? ticketData.AssigneeId;
          if (currentAssigneeId && !sg?.Members.some(m => m.Id == currentAssigneeId))
            ticketData.AssigneeId = 0;
        }
        break;
      case Assignment.ElementId:
        const assignmentId = parseInt(value);
        if(assignmentId != -1)
          ticketData.AssigneeId = assignmentId;
        if (cardTemplateJsonValue?.body) {
          const elements: any[] = cardTemplateJsonValue.body;
          const element = elements.find(e => e["id"] == key);
          const assignedTeam = currentState?.teams.find(x => x.TeamsAadObjectId == element["team"]);
          const assignmentTeamId = assignedTeam?.Id ?? 0;
          if (assignmentTeamId != 0)
            ticketData.TeamId = assignmentTeamId;
          let assignmentGroupId = element["group"] ? parseInt(element["group"]) : -1;
          const assignedTeamChannelid = assignedTeam?.TeamsChannelId ?? "";
          assignmentGroupId = assignmentGroupId == 0 ? currentState?.supportgroups.find(x => x.TeamsChannelId == assignedTeamChannelid)?.Id ?? 0 : assignmentGroupId;
          if (assignmentGroupId > 0)
            ticketData.SupportGroupId = assignmentGroupId;
          else
            ticketData.SupportGroupId = null;
        }
        break;
      case DueDate.ElementId:
        if (value)
          ticketData.DueDate = value;
        break;
      case ResolutionDate.ElementId:
        if (value)
          ticketData.ResolutionDate = value;
        break;
      default:
        if (cardTemplateJsonValue?.body) {
          const elements: any[] = cardTemplateJsonValue.body;
          const element = elements.find(e => e["id"] == key);
          if (element?.isCustomProperty){
            const cProp = customProperties.find(cp => cp.PropertyId == key);
            if(cProp){
              if(value){
                const formCustomPropsStr = ticketData?.CustomPropertiesAnswerJson ?? "{}";
                const formCustomPropsJson = JSON.parse((formCustomPropsStr?.length ?? 0) > 0 ? formCustomPropsStr : "{}");
                
                switch(CustomPropertyDataType_LABELS[cProp.DataType]){
                  case CustomPropertyDataType_LABELS.TIME:
                    if(!isDateISOFormat(value))
                      formCustomPropsJson[key] = getCardTimeInputISOFormatted(value);
                    break;
                  case CustomPropertyDataType_LABELS.DATE:
                    formCustomPropsJson[key] = getCardDateInputISOFormatted(value);
                    break;
                  default:
                    formCustomPropsJson[key] = value;
                    break;
                }
                ticketData.CustomPropertiesAnswerJson = JSON.stringify(formCustomPropsJson);
              }
            }
          }
        }
        break;
    }
  });
}